import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, openCourseFile } from '../../lib/api'
import { QuickScan } from '../../components/parent/QuickScan'
import { GradeProgressChart } from '../../components/parent/GradeProgressChart'
import {
  ArrowLeft,
  TrendingUp,
  FileText,
  Calendar,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Trophy,
  BarChart3,
  ChevronRight,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import type { UpcomingWorkData } from '../../lib/api-types'

interface CourseWithGrade {
  id: number
  name: string
  term: string
  current_score: number | null
  current_grade: string | null
  missing_count: number
}

interface StudentProfile {
  student: { id: number; name: string; canvas_id: number }
  profile: {
    level: number
    title: string
    total_xp: number
    xp_in_level: number
    xp_for_next: number
    progress_pct: number
    study_streak: number
    longest_streak: number
    streak_shields: number
    total_achievements: number
    total_challenges_completed: number
  }
  courses: CourseWithGrade[]
  academic: {
    average_grade: number | null
    total_missing: number
    grade_updated_at: string | null
  }
}

interface HomeworkFile {
  id: number
  name: string
  category: string
  size_bytes: number | null
  size_mb: number | null
  lesson_start: number
  lesson_end: number
  has_local: boolean
  url: string
}

interface Assignment {
  id: number
  title: string
  course: string
  course_id: number
  subject: string | null
  lesson_number: number | null
  is_test: boolean
  is_spelling: boolean
  start_at: string | null
  has_scan: boolean
  files: HomeworkFile[]
  file_count: number
}

interface HomeworkData {
  student: { id: number; name: string }
  date: string
  assignments: Assignment[]
  stats: {
    total_assignments: number
    with_files: number
    tests: number
    total_files: number
  }
}

export default function ParentStudentDetail() {
  const { studentId } = useParams()
  const queryClient = useQueryClient()
  const studentIdNum = parseInt(studentId || '0', 10)

  // Fetch gamification/profile data (from PostgreSQL)
  const { data: profile, isLoading: profileLoading } = useQuery<StudentProfile>({
    queryKey: ['parent-student', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}`)
      return response.data
    },
    enabled: !!studentId,
  })

  // Fetch today's homework with files (from PostgreSQL)
  const { data: homeworkData } = useQuery<HomeworkData>({
    queryKey: ['todays-homework', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/todays-homework`)
      return response.data
    },
    enabled: !!studentId,
  })

  // Fetch upcoming work for tomorrow preview (from PostgreSQL)
  const { data: upcomingWork } = useQuery<UpcomingWorkData>({
    queryKey: ['parent-student-upcoming', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/upcoming-work?days=7`)
      return response.data
    },
    enabled: !!studentId,
  })

  if (profileLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="h-32 bg-surface-card rounded-xl"></div>
        <div className="h-64 bg-surface-card rounded-xl"></div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">Student not found</p>
        <Link to="/parent" className="btn btn-primary mt-4">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  const handleDownload = (fileId: number) => {
    openCourseFile(fileId)
  }

  const assignments = homeworkData?.assignments || []
  const testsToday = upcomingWork?.tests_today || []
  const testsTomorrow = upcomingWork?.tests_tomorrow || []
  const tomorrowWork = upcomingWork?.tomorrow || []

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/parent" className="p-2 hover:bg-surface-hover rounded-lg">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{profile.student.name}</h1>
          <p className="text-gray-400 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="text-right">
          <div className="level-badge text-xl">{profile.profile.level}</div>
          <p className="text-sm text-gray-400 mt-1">{profile.profile.title}</p>
        </div>
      </div>

      {/* Test Alerts */}
      {(testsToday.length > 0 || testsTomorrow.length > 0) && (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <p className="font-bold text-red-300">Test Alert!</p>
          </div>
          {testsToday.map((test) => (
            <p key={test.id} className="text-sm ml-9">
              <span className="text-red-400 font-medium">TODAY:</span> {test.title} ({test.course})
            </p>
          ))}
          {testsTomorrow.map((test) => (
            <p key={test.id} className="text-sm ml-9">
              <span className="text-orange-400 font-medium">TOMORROW:</span> {test.title} ({test.course})
            </p>
          ))}
        </div>
      )}

      {/* Missing Assignments Alert */}
      {profile.academic.total_missing > 0 && (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <p className="font-bold text-red-300">
              {profile.academic.total_missing} Missing Assignment{profile.academic.total_missing > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Tonight's Homework - With Inline Files */}
      <div className="card">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-quest-primary" />
          Tonight's Homework
          {homeworkData?.stats.total_files && homeworkData.stats.total_files > 0 && (
            <span className="ml-auto text-sm font-normal text-blue-400">
              {homeworkData.stats.total_files} printable files
            </span>
          )}
        </h2>

        {assignments.length > 0 ? (
          <div className="space-y-3">
            {assignments.filter(a => !a.is_test).map((assignment) => (
              <div
                key={assignment.id}
                className="p-3 bg-surface-card/50 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    {assignment.has_scan ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {assignment.subject && (
                          <span className="text-quest-primary">{assignment.subject} </span>
                        )}
                        {assignment.lesson_number && (
                          <span className="text-gray-400">L.{assignment.lesson_number}</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-400">{assignment.title}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">{assignment.course}</span>
                </div>

                {/* Inline Files */}
                {assignment.files.length > 0 && (
                  <div className="mt-3 ml-8 flex flex-wrap gap-2">
                    {assignment.files.map((file) => (
                      <button
                        key={file.id}
                        onClick={() => handleDownload(file.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                        title={file.name}
                      >
                        <Download className="w-4 h-4" />
                        <span className="truncate max-w-[150px]">
                          {file.category === 'homework' ? 'Homework' :
                           file.category === 'textbook' ? 'Textbook' :
                           file.category === 'worksheet' ? 'Worksheet' : 'PDF'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No homework due today</p>
          </div>
        )}
      </div>

      {/* Tomorrow Preview */}
      {tomorrowWork.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-400" />
            Tomorrow Preview
          </h2>
          <div className="space-y-2">
            {tomorrowWork.map((item) => (
              <div
                key={item.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  item.is_test ? 'bg-orange-900/20 border border-orange-500/30' : 'bg-surface-card/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-gray-400">{item.course}</p>
                  </div>
                </div>
                {item.is_test && (
                  <span className="px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-400">TEST</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grade Progress Chart */}
      <GradeProgressChart
        studentId={studentIdNum}
        studentName={profile.student.name}
      />

      {/* Current Grades */}
      <div className="card">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-quest-primary" />
          Current Grades
          {profile.academic.average_grade && (
            <span className="ml-auto text-sm font-normal text-gray-400">
              Average: <span className={profile.academic.average_grade >= 80 ? 'text-green-400' : profile.academic.average_grade >= 70 ? 'text-yellow-400' : 'text-red-400'}>{profile.academic.average_grade}%</span>
            </span>
          )}
        </h2>
        <div className="space-y-3">
          {profile.courses.filter(c => c.current_score !== null).map((course) => (
            <Link
              key={course.id}
              to={`/parent/student/${studentId}/course/${course.id}`}
              className="flex items-center justify-between p-3 bg-surface-card/50 rounded-lg hover:bg-surface-hover transition-colors group"
            >
              <div className="flex-1">
                <p className="font-medium">{course.name.split(' - ')[0]}</p>
                {course.missing_count > 0 && (
                  <p className="text-xs text-red-400">{course.missing_count} missing</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`text-lg font-bold ${
                  course.current_score && course.current_score >= 90 ? 'text-green-400' :
                  course.current_score && course.current_score >= 80 ? 'text-blue-400' :
                  course.current_score && course.current_score >= 70 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {course.current_score?.toFixed(1)}%
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-300 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Sync info */}
      {upcomingWork?.last_sync && (
        <p className="text-center text-xs text-gray-600">
          Data synced: {formatDistanceToNow(new Date(upcomingWork.last_sync), { addSuffix: true })}
        </p>
      )}

      {/* Quick Scan FAB */}
      {studentIdNum > 0 && (
        <QuickScan
          studentId={studentIdNum}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['todays-homework', studentId] })
            queryClient.invalidateQueries({ queryKey: ['parent-student', studentId] })
          }}
        />
      )}

      {/* Bottom Navigation Tabs */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface-base border-t border-gray-800 p-2">
        <div className="max-w-4xl mx-auto flex justify-around">
          <Link
            to={`/parent/student/${studentId}`}
            className="flex flex-col items-center gap-1 p-2 text-quest-primary"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs">Homework</span>
          </Link>
          <Link
            to={`/parent/student/${studentId}/scans`}
            className="flex flex-col items-center gap-1 p-2 text-gray-400 hover:text-gray-200"
          >
            <BarChart3 className="w-5 h-5" />
            <span className="text-xs">Scans</span>
          </Link>
          <Link
            to={`/parent/student/${studentId}/progress`}
            className="flex flex-col items-center gap-1 p-2 text-gray-400 hover:text-gray-200"
          >
            <Trophy className="w-5 h-5" />
            <span className="text-xs">Progress</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
